import angular from 'angular'

// Enough time for the frame transition animation (0.5s) to complete before we can start the next queued transition
const FRAME_TRANSITION_TIMEOUT = 550

/**
 * Controller for the vd-carousel component.
 *
 * @class VdCarouselController
 */
export default class VdCarouselController {
  constructor($q, $element, $scope, $timeout) {
    'ngInject'
    this._$q = $q
    this._$element = $element
    this._$scope = $scope
    this._$timeout = $timeout
  }

  $onInit() {
    this.frames = []
  }

  $postLink() {
    this._$element.on('touchstart', event => this._onTouchStart(event))
    this._$element.on('touchmove', event => this._onTouchMove(event))
    this._$element.on('touchend', event => this._onTouchEnd(event))

    this._activeIndex = parseInt(this.initialFrame || 0, 10)
    this._initActiveFrame()
  }

  // @todo handle a frame de-registering? e.g. on $destroy? Currently not a supported use case, all frames must be
  // present at initial render and remain present until the carousel is destroyed and not controlled via ng-if
  // for example.

  /**
   * Register a vd-carousel-frame with this carousel.
   *
   * @method registerFrame
   *
   * @param  {VdCarouselFrameController} frame
   *         Instance of the vd-carousel-frame's controller
   */
  registerFrame(frame) {
    this.frames.push(frame)

    if (
      angular.isNumber(this._activeIndex) &&
      this._activeIndex === this.frames.length - 1
    ) {
      this._initActiveFrame()
    }
  }

  /**
   * Show the frame at the given index. If there is already a frame transition in progress the change will be
   * scheduled to happen after the current transition.
   *
   * @method showFrame
   *
   * @param  {Number} index
   *         The index of the frame to show
   * @param  {String} transitionDirection
   *         The direction the frames should transition in/out. Supported values are 'left' or 'right'.
   */
  showFrame(index, transitionDirection) {
    const frame = this.frames[index]
    if (!frame) {
      return
    }

    this._activeIndex = index

    if (this._transitionInProgress) {
      if (this._nextFrameTransition) {
        this._nextFrameTransition.reject()
      }

      this._nextFrameTransition = this._$q.defer()
      this._nextFrameTransition.promise.then(
        () => {
          this._nextFrameTransition = null
          this.showFrame(index, transitionDirection)
        },
        () => {}
      )

      return
    }

    this.frames.some((frame, prevIndex) => {
      if (!frame.active) {
        return false
      }

      if (index === prevIndex) {
        transitionDirection = ''
      } else {
        if (!transitionDirection) {
          transitionDirection = index < prevIndex ? 'right' : 'left'
        }
        frame.setActive(false, transitionDirection)
      }

      return true
    })

    frame.setActive(true, transitionDirection)

    this._transitionInProgress = this._$timeout(() => {
      this._transitionInProgress = null
      if (this._nextFrameTransition) {
        this._nextFrameTransition.resolve()
      }
    }, FRAME_TRANSITION_TIMEOUT)
  }

  /**
   * Show the next frame. If we are currently showing the last frame in the list the next frame is the first frame in
   * the list.
   *
   * @method showNextFrame
   */
  showNextFrame() {
    const index =
      this._activeIndex === this.frames.length - 1 ? 0 : this._activeIndex + 1
    this.showFrame(index, 'left')
  }

  /**
   * Show the previous frame. If we are currently showing the first frame in the list the previous frame is the last
   * frame in the list.
   *
   * @method showPreviousFrame
   */
  showPreviousFrame() {
    const index =
      (this._activeIndex === 0 ? this.frames.length : this._activeIndex) - 1
    this.showFrame(index, 'right')
  }

  /**
   * Set the initial active frame as active (if it exists yet).
   *
   * @private
   * @method _initActiveFrame
   */
  _initActiveFrame() {
    const frame = this.frames[this._activeIndex]
    if (frame) {
      frame.setActive(true)
    }
  }

  /**
   * Handle touchstart event. Caches event information for determining swipe gesture in touchmove/touchend handlers.
   *
   * @private
   * @method _onTouchStart
   * @param  {Event} event
   */
  _onTouchStart(event) {
    const touches = event.changedTouches
    this._touchStart = touches.length === 1 ? touches[0] : null
  }

  /**
   * Handle touchmove event. Prevents default (browser scroll) if this appears to be a swipe gesture.
   *
   * @private
   * @method _onTouchMove
   * @param  {Event} event
   */
  _onTouchMove(event) {
    const { isSwipe } = this._getSwipeData(event)
    if (isSwipe) {
      event.preventDefault()
    }
  }

  /**
   * Handle touchend event. Determines whether a swipe gesture was performed and transitions to the next/previous frame
   * if applicable.
   *
   * @private
   * @method _onTouchEnd
   * @param  {Event} event
   */
  _onTouchEnd(event) {
    const { isSwipe, deltaX } = this._getSwipeData(event)

    if (isSwipe && Math.abs(deltaX) > this._$element[0].clientWidth / 4) {
      this._$scope.$apply(() => {
        if (deltaX < 0) {
          this.showNextFrame()
        } else {
          this.showPreviousFrame()
        }
      })
    }
  }

  /**
   * Get the swipe data for the given touch event.
   *
   * @private
   * @method _getSwipeData
   * @param  {Event} event
   *
   * @return {{ isSwipe: Boolean, deltaX: Number }} isSwipe will be true if this is a horizontal swipe gesture, deltaX
   *         is the number of pixels change from the starting X position to the current X position
   */
  _getSwipeData(event) {
    const touches = event.changedTouches
    const touchEnd = touches[0]

    if (
      !this._touchStart ||
      touchEnd.identifier !== this._touchStart.identifier
    ) {
      return { isSwipe: false }
    }

    const deltaX = touchEnd.clientX - this._touchStart.clientX
    const deltaY = touchEnd.clientY - this._touchStart.clientY

    const isSwipe = Math.abs(deltaX) > Math.abs(deltaY)

    return { isSwipe, deltaX }
  }
}
